import type Phaser from "phaser";

const FRAME_ID = "ui-safe-frame";

/**
 * Keep `#ui-safe-frame` aligned to the Phaser canvas CSS rect (FIT letterbox).
 */
export class SafeFrame {
  private readonly frame: HTMLElement;
  private readonly onResize: () => void;
  private detached = false;

  constructor(
    private readonly game: Phaser.Game,
    overlayHost: HTMLElement,
  ) {
    let frame = overlayHost.querySelector<HTMLElement>(`#${FRAME_ID}`);
    if (!frame) {
      frame = document.createElement("div");
      frame.id = FRAME_ID;
      overlayHost.appendChild(frame);
    }
    this.frame = frame;
    this.onResize = () => this.sync();
    this.game.scale.on("resize", this.onResize);
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
    window.visualViewport?.addEventListener("resize", this.onResize);
    // First paint after canvas is in DOM
    requestAnimationFrame(() => this.sync());
  }

  get element(): HTMLElement {
    return this.frame;
  }

  sync(): void {
    if (this.detached) return;
    const canvas = this.game.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const host = this.frame.offsetParent as HTMLElement | null;
    const hostRect = host?.getBoundingClientRect() ?? {
      left: 0,
      top: 0,
    };
    // Whole CSS px — avoids subpixel blur on HUD text over FIT canvas.
    this.frame.style.left = `${Math.round(rect.left - hostRect.left)}px`;
    this.frame.style.top = `${Math.round(rect.top - hostRect.top)}px`;
    this.frame.style.width = `${Math.max(1, Math.round(rect.width))}px`;
    this.frame.style.height = `${Math.max(1, Math.round(rect.height))}px`;
  }

  destroy(): void {
    this.detached = true;
    this.game.scale.off("resize", this.onResize);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
    window.visualViewport?.removeEventListener("resize", this.onResize);
  }
}
